import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { TableCell } from '../../../components/table/TableCell';
import React from 'react';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck } from '@fortawesome/free-solid-svg-icons';
import { IFinancialAccount } from '../../payments/payments-interfaces-helper';

export enum FinancialAccountTableAccessorKeys {
  relatedEntityId = 'relatedEntityId',
  relatedEntityType = 'relatedEntityType',
  accountType = 'accountType',
  accountNumber = 'maskedAccountNumber',
  routingNumber = 'maskedRoutingNumber',
  bankName = 'bankName',
  supportACH = 'supportACH',
  supportWire = 'supportWire',
  currency = 'currency',
}

export const defaultFinancialAccountColumnSizing: ColumnSizing = {
  [FinancialAccountTableAccessorKeys.relatedEntityId]: 100,
  [FinancialAccountTableAccessorKeys.relatedEntityType]: 100,
  [FinancialAccountTableAccessorKeys.accountType]: 100,
  [FinancialAccountTableAccessorKeys.accountNumber]: 100,
  [FinancialAccountTableAccessorKeys.routingNumber]: 100,
  [FinancialAccountTableAccessorKeys.bankName]: 100,
  [FinancialAccountTableAccessorKeys.currency]: 50,
  [FinancialAccountTableAccessorKeys.supportACH]: 50,
  [FinancialAccountTableAccessorKeys.supportWire]: 50,
};

export const defaultFinancialAccountColumns: MRT_ColumnDef<IFinancialAccount>[] = [
  {
    accessorKey: FinancialAccountTableAccessorKeys.relatedEntityId,
    header: 'Related entity',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Related entity</span>,
    Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={true}
      >
        <RelatedEntityNameLink
          id={renderedCellValue}
          type={row?.original?.relatedEntityType}
          redirectUrl={row?.original?.relatedEntityType === RELATED_ENTITY_TYPE.MERCHANT && `/accounts/merchants/${renderedCellValue}/bank`}
        />
      </TableCell>
    )
  },
  {
    accessorKey: FinancialAccountTableAccessorKeys.relatedEntityType,
    header: 'Related entity type',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Related entity type</span>,
    Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={false}
      >
        {renderedCellValue ? renderedCellValue : '-'}
      </TableCell>
    )
  },
  {
    accessorKey: FinancialAccountTableAccessorKeys.bankName,
    header: 'Bank',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Bank</span>,
    Cell: ({ renderedCellValue, table }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={false}
      >
        {renderedCellValue ? renderedCellValue : '-'}
      </TableCell>
    )
  },
  {
    accessorKey: FinancialAccountTableAccessorKeys.accountType,
    header: 'Account type',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Account type</span>,
    Cell: ({ renderedCellValue, table }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={false}
      >
        {renderedCellValue ? renderedCellValue : '-'}
      </TableCell>
    )
  },
  {
    accessorKey: FinancialAccountTableAccessorKeys.accountNumber,
    header: 'Account #',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Account #</span>,
    Cell: ({ renderedCellValue, table }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={false}
      >
        {renderedCellValue ? renderedCellValue : '-'}
      </TableCell>
    )
  },
  {
    accessorKey: FinancialAccountTableAccessorKeys.routingNumber,
    header: 'Routing #',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Routing #</span>,
    Cell: ({ renderedCellValue, table }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={false}
      >
        {renderedCellValue ? renderedCellValue : '-'}
      </TableCell>
    )
  },
  {
    accessorKey: FinancialAccountTableAccessorKeys.currency,
    header: 'Currency',
    enableSorting: false,
    Header: <span className="text-muted fw-bolder main-font-family">Currency</span>,
    Cell: ({ renderedCellValue, table }: any): JSX.Element => (
      <TableCell
        additionalClass="main-font-family text-black fs-7"
        density={table?.options?.state?.density}
        isTextOverflow={false}
      >
        {renderedCellValue ? renderedCellValue : '-'}
      </TableCell>
    )
  },
  {
    id: 'supportedTypes',
    header: 'Supported types',
    columns: [
      {
        accessorKey: FinancialAccountTableAccessorKeys.supportACH,
        header: 'ACH',
        enableSorting: false,
        Header: <span className="text-muted fw-bolder main-font-family">ACH</span>,
        Cell: ({ renderedCellValue, table }: any): JSX.Element => (
          <TableCell
            additionalClass="main-font-family text-black fs-7"
            density={table?.options?.state?.density}
            isTextOverflow={false}
          >
            {renderedCellValue && <FontAwesomeIcon icon={faSquareCheck} width={20} className="text-danger h-20px"/>}
          </TableCell>
        )
      },
      {
        accessorKey: FinancialAccountTableAccessorKeys.supportWire,
        header: 'Wire',
        enableSorting: false,
        Header: <span className="text-muted fw-bolder main-font-family">Wire</span>,
        Cell: ({ renderedCellValue, table }: any): JSX.Element => (
          <TableCell
            additionalClass="main-font-family text-black fs-7"
            density={table?.options?.state?.density}
            isTextOverflow={false}
          >
            {renderedCellValue && <FontAwesomeIcon icon={faSquareCheck} width={20} className="text-danger h-20px"/>}
          </TableCell>
        )
      },
    ],
  },
];
